import { pickConversationSearch } from '#/shared/lib/table'
import type { ConversationSearch } from '#/shared/lib/table'

import { CAMPAIGN_CONTACT_FILTER_ORDER } from './contact-filters'
import type { CampaignContactFilter } from './contact-filters'

export const CAMPAIGN_DETAIL_TABS = {
  OVERVIEW: 'overview',
  SEQUENCE: 'sequence',
  CONTACT: 'contact',
  ACTIVITY: 'activity',
} as const

export type CampaignDetailTab =
  (typeof CAMPAIGN_DETAIL_TABS)[keyof typeof CAMPAIGN_DETAIL_TABS]

export const CAMPAIGN_DETAIL_DEFAULT_TAB: CampaignDetailTab =
  CAMPAIGN_DETAIL_TABS.OVERVIEW

export const CAMPAIGN_DETAIL_TAB_ORDER: readonly CampaignDetailTab[] = [
  CAMPAIGN_DETAIL_TABS.OVERVIEW,
  CAMPAIGN_DETAIL_TABS.SEQUENCE,
  CAMPAIGN_DETAIL_TABS.CONTACT,
  CAMPAIGN_DETAIL_TABS.ACTIVITY,
]

export function campaignDetailTabLabelKey(tab: CampaignDetailTab): string {
  return `campaigns.detail.tabs.${tab}`
}

export interface CampaignDetailSearch extends ConversationSearch {
  tab: CampaignDetailTab
  /** The Contact tab's opening chip — set by a link into one slice (the
   *  waiting band's headline), read once when the tab mounts. */
  filter?: CampaignContactFilter
}

function isDetailTab(value: unknown): value is CampaignDetailTab {
  return (
    typeof value === 'string' &&
    CAMPAIGN_DETAIL_TAB_ORDER.includes(value as CampaignDetailTab)
  )
}

function isContactFilter(value: unknown): value is CampaignContactFilter {
  return (
    typeof value === 'string' &&
    CAMPAIGN_CONTACT_FILTER_ORDER.includes(value as CampaignContactFilter)
  )
}

/** Keeps an unknown `?tab=` from stranding the page on a body it cannot render,
 *  and carries the global phone-panel params through — see
 *  `pickConversationSearch`. */
export function validateCampaignDetailSearch(
  search: Record<string, unknown>,
): CampaignDetailSearch {
  const tab = search['tab']
  const filter = search['filter']
  return {
    tab: isDetailTab(tab) ? tab : CAMPAIGN_DETAIL_DEFAULT_TAB,
    filter: isContactFilter(filter) ? filter : undefined,
    ...pickConversationSearch(search),
  }
}
